'use client';

import { useCallback, useEffect, lazy, Suspense, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleNotchIcon, IdentificationCardIcon, PlusIcon, ShieldCheckIcon, ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFinance } from '@/hooks/useFinance';
import { useProfile } from '@/hooks/queries/useProfile';
import { ApiError } from '@/lib/api';
import { buildConnectRedirectUri } from '@/lib/connectAttempt';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { PLUGGY_DEBUG_MODE } from '@/lib/env';
import { track } from '@/lib/telemetry';
import type { ConnectEventPayload } from 'react-pluggy-connect';

const PluggyConnectWidget = lazy(() =>
  import('react-pluggy-connect').then((mod) => ({ default: mod.PluggyConnect })),
);

export interface PluggyConnectSession {
  connectToken: string;
  openFinanceCpf: string | null;
  itemId?: string;
  selectedConnectorId?: number;
  allowedConnectorIds?: number[];
}

interface PluggyConnectInnerProps {
  onLaunch: (session: PluggyConnectSession) => void;
  onClose?: () => void;
  itemId?: string;
  autoOpen?: boolean;
  selectedConnectorId?: number;
  allowedConnectorIds?: number[];
  launchDisabled?: boolean;
}

interface PluggyConnectModalProps {
  session: PluggyConnectSession;
  onSuccess: (pluggyItemId: string) => void;
  onClose: () => void;
  onError?: (message: string) => void;
}

/**
 * Preflight surface for Pluggy Connect. It handles profile/CPF/token loading,
 * then hands the fully prepared session to a body-level Pluggy modal.
 */
export function PluggyConnectInner({
  onLaunch,
  onClose,
  itemId,
  autoOpen = false,
  selectedConnectorId,
  allowedConnectorIds,
  launchDisabled = false,
}: PluggyConnectInnerProps) {
  const { getConnectToken, loading, error } = useFinance();
  // Shared cache, not an ad-hoc read (VER-387): the Validação step writes the CPF
  // and mutates this key, so the preflight sees it without a refetch.
  const { data: profile, error: profileError, mutate: refetchProfile } = useProfile();
  const [connectToken, setConnectToken] = useState<string | null>(null);
  const [openFinanceCpf, setOpenFinanceCpf] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [autoOpenedOnce, setAutoOpenedOnce] = useState(false);
  const needsCpf = profile !== undefined && !profile.hasCpf;

  const loadConnectToken = useCallback(async () => {
    // Return the OAuth hop to the page it STARTED from. The old fixed
    // '/connections' sent onboarding users through the dashboard, where the
    // platform gate bounced the fresh page load back to /onboarding with all
    // local import state gone (staging, 2026-08-22). The builder also carries
    // the storage-blocked discovery fallback for the onboarding step.
    const redirectUri = typeof window !== 'undefined'
      ? buildConnectRedirectUri(window.location, Date.now(), itemId ?? null)
      : undefined;

    const response = await getConnectToken(itemId, redirectUri);
    setConnectToken(response.accessToken);
    setOpenFinanceCpf(response.cpf);
    return response;
  }, [getConnectToken, itemId]);

  // The CPF gate now reads the shared profile: while it is unresolved or missing
  // a CPF, no token is fetched — the panel below routes the user to /onboarding,
  // where the Validação step is the ONE place a CPF is registered (VER-388).
  const prepareConnection = useCallback(async () => {
    if (profile === undefined || !profile.hasCpf) return;
    await loadConnectToken();
  }, [loadConnectToken, profile]);

  const handlePrepareError = useCallback((err: unknown) => {
    console.error('[PluggyConnect] Failed to prepare connection:', err);
    // A plan-limit 402 already raised the global upgrade dialog, and useFinance's
    // error state carries the short summary — echoing the raw server body here
    // would show it twice.
    if (err instanceof ApiError && err.isLimitExceeded()) return;
    setLocalError(err instanceof Error ? err.message : 'Erro ao preparar conexão');
  }, []);

  useEffect(() => {
    let cancelled = false;
    prepareConnection().catch((err: unknown) => {
      if (cancelled) return;
      handlePrepareError(err);
    });
    return () => {
      cancelled = true;
    };
  }, [handlePrepareError, prepareConnection]);

  // A failed preparation used to leave the launch button spinning "Preparando..."
  // forever (disabled on !connectToken, and nothing ever retried) — the retry
  // path below is what makes that state escapable.
  const handleRetryPrepare = useCallback(() => {
    setLocalError(null);
    // A failed /users/me leaves `profile` undefined and prepareConnection
    // returning early — retrying the token alone would spin forever. Revalidate
    // the profile too; when it resolves, the prepare effect re-runs by itself.
    void refetchProfile();
    void prepareConnection().catch(handlePrepareError);
  }, [handlePrepareError, prepareConnection, refetchProfile]);

  const launchConnect = useCallback(
    (token: string | null = connectToken, cpf: string | null = openFinanceCpf) => {
      if (!token) return;
      setLocalError(null);
      onLaunch({
        connectToken: token,
        openFinanceCpf: cpf,
        itemId,
        selectedConnectorId,
        allowedConnectorIds,
      });
    },
    [allowedConnectorIds, connectToken, itemId, onLaunch, openFinanceCpf, selectedConnectorId],
  );

  useEffect(() => {
    if (autoOpen && connectToken && !autoOpenedOnce && !needsCpf && !launchDisabled) {
      setAutoOpenedOnce(true);
      launchConnect(connectToken, openFinanceCpf);
    }
  }, [autoOpen, autoOpenedOnce, connectToken, launchConnect, launchDisabled, needsCpf, openFinanceCpf]);

  const profileLoadError =
    profileError && profile === undefined ? 'Não foi possível carregar seu perfil.' : null;
  const displayError = localError || error || profileLoadError;

  return (
    <div className="flex flex-col gap-3">
      {needsCpf ? (
        <CpfMissingPanel onClose={onClose} />
      ) : autoOpen ? null : (
        <>
          <div className="rounded-xl border border-success-border bg-success-surface/60 p-3">
            <div className="flex items-center gap-2">
              <ShieldCheckIcon size={16} weight="fill" className="text-success-text" />
              <p className="text-2xs font-bold tracking-[0.18em] text-success-text uppercase">Dados solicitados</p>
            </div>
            <p className="mt-2 text-2xs leading-relaxed text-success-text/80">
              Solicitamos todos os dados que a instituição disponibiliza via Open Finance — dados cadastrais,
              contas, cartões e investimentos, quando houver. Você revê e autoriza tudo no app do seu banco.
            </p>
            <p className="mt-2 text-2xs leading-relaxed text-success-text/80">
              Conexão via Open Finance regulamentada pelo Banco Central. Você pode revogar o acesso a qualquer momento.
            </p>
          </div>

          {displayError && !connectToken && !loading ? (
            <Button onClick={handleRetryPrepare} variant="outline" className="w-full" size="lg">
              <ArrowsClockwiseIcon className="size-5" weight="bold" />
              Tentar novamente
            </Button>
          ) : (
          <Button
            onClick={() => launchConnect()}
            disabled={loading || !connectToken || launchDisabled}
            className="w-full"
            size="lg"
          >
            {loading || !connectToken || launchDisabled ? (
              <>
                <CircleNotchIcon className="size-5 animate-spin" />
                Preparando...
              </>
            ) : itemId ? (
              <>
                <ArrowsClockwiseIcon className="size-5" weight="bold" />
                Atualizar conexão
              </>
            ) : (
              <>
                <PlusIcon className="size-5" weight="bold" />
                Conectar agora
              </>
            )}
          </Button>
          )}
        </>
      )}

      {displayError && <Alert variant="destructive">{displayError}</Alert>}
    </div>
  );
}

function CpfMissingPanel({ onClose }: { onClose?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Inside /onboarding the Validação step IS the CPF surface — a link there
  // would be a no-op loop, so the panel only explains.
  const alreadyOnboarding = location.pathname === '/onboarding';

  const goToOnboarding = useCallback(() => {
    navigate('/onboarding');
  }, [navigate]);

  return (
    <div className="w-full space-y-3 rounded-xl border border-border-subtle bg-neutral-50 p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-700">
          <IdentificationCardIcon size={20} weight="fill" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-text-primary">Falta cadastrar seu CPF</p>
          <p className="mt-1 text-xs/5 text-text-secondary">
            O CPF do titular é obrigatório para conectar pelo Open Finance. Cadastre na etapa de
            validação e volte para conectar.
          </p>
        </div>
      </div>
      {!alreadyOnboarding && (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {onClose && (
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
          )}
          <Button type="button" onClick={goToOnboarding}>
            <IdentificationCardIcon className="size-5" />
            Cadastrar CPF
          </Button>
        </div>
      )}
    </div>
  );
}

export function PluggyConnectModal({ session, onSuccess, onClose, onError }: PluggyConnectModalProps) {
  const debugMode = PLUGGY_DEBUG_MODE;

  useEffect(() => {
    document.body.classList.add('pluggy-widget-open');
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      const win = window as typeof window & {
        __verticeLastPluggySession?: {
          itemId?: string;
          selectedConnectorId?: number;
          allowedConnectorIds?: number[];
        };
      };
      win.__verticeLastPluggySession = {
        itemId: session.itemId,
        selectedConnectorId: session.selectedConnectorId,
        allowedConnectorIds: session.allowedConnectorIds,
      };
    }
    return () => {
      document.body.classList.remove('pluggy-widget-open');
    };
  }, [session.allowedConnectorIds, session.itemId, session.selectedConnectorId]);

  const handleSuccess = useCallback((data: { item: { id: string } }) => {
    onSuccess(data.item.id);
  }, [onSuccess]);

  const handleError = useCallback((err: { message?: string; code?: string }) => {
    console.error('[PluggyConnect] Error:', err);
    track('connect.widget_error', { stage: 'widget' });
    onError?.(err.message || 'Erro ao conectar');
  }, [onError]);

  const handleEvent = useCallback((payload: ConnectEventPayload) => {
    // DEV-only: session replay records console output, so widget lifecycle
    // events must not reach the console in production.
    if (import.meta.env.DEV) console.log('[PluggyConnect] Event:', payload.event);
  }, []);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <button
        type="button"
        onClick={onClose}
        className="fixed top-[max(1rem,env(safe-area-inset-top))] right-[max(1rem,env(safe-area-inset-right))] z-5000000001 flex size-10 items-center justify-center rounded-full border border-border-subtle bg-white text-text-secondary shadow-lg transition hover:bg-neutral-50 hover:text-text-primary"
        aria-label="Fechar"
      >
        <svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
      <Suspense fallback={null}>
        <PluggyConnectWidget
          connectToken={session.connectToken}
          includeSandbox={debugMode}
          updateItem={session.itemId}
          openFinanceParameters={session.openFinanceCpf ? { cpf: session.openFinanceCpf } : undefined}
          selectedConnectorId={session.selectedConnectorId}
          connectorIds={session.allowedConnectorIds}
          onSuccess={handleSuccess}
          onError={handleError}
          onClose={onClose}
          onEvent={handleEvent}
        />
      </Suspense>
    </>,
    document.body,
  );
}

/**
 * Backwards-compat wrapper that renders the launcher and body-level Pluggy modal
 * together for any legacy caller still using this component directly.
 */
export function PluggyConnect({
  onSuccess,
  onClose,
  itemId,
  autoOpen,
  selectedConnectorId,
  allowedConnectorIds,
}: {
  onSuccess: (pluggyItemId: string) => void;
  onClose?: () => void;
  itemId?: string;
  autoOpen?: boolean;
  selectedConnectorId?: number;
  allowedConnectorIds?: number[];
}) {
  const [session, setSession] = useState<PluggyConnectSession | null>(null);

  const handleClose = useCallback(() => {
    setSession(null);
    onClose?.();
  }, [onClose]);

  const handleSuccess = useCallback((pluggyItemId: string) => {
    setSession(null);
    onSuccess(pluggyItemId);
  }, [onSuccess]);

  return (
    <>
      <PluggyConnectInner
        itemId={itemId}
        autoOpen={autoOpen}
        selectedConnectorId={selectedConnectorId}
        allowedConnectorIds={allowedConnectorIds}
        onLaunch={setSession}
        onClose={onClose}
      />
      {session && (
        <PluggyConnectModal
          session={session}
          onSuccess={handleSuccess}
          onClose={handleClose}
        />
      )}
    </>
  );
}
